export const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "oldest", label: "最早发布" },
  { value: "hot", label: "最热优先" },
] as const;

export type ArticleSortKey = (typeof sortOptions)[number]["value"];

interface ArticleFilterPanelProps {
  topics: string[];
  topic: string;
  onTopicChange: (value: string) => void;
  sort: ArticleSortKey;
  onSortChange: (value: ArticleSortKey) => void;
  tags: string[];
  activeTags: string[];
  onTagToggle: (tag: string) => void;
  onReset: () => void;
  open: boolean;
  onToggle: () => void;
  resultCount: number;
}

export default function ArticleFilterPanel({
  topics,
  topic,
  onTopicChange,
  sort,
  onSortChange,
  tags,
  activeTags,
  onTagToggle,
  onReset,
  open,
  onToggle,
  resultCount,
}: ArticleFilterPanelProps) {
  const hasFilter = topic !== "全部主题" || activeTags.length > 0;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center gap-3 border-b border-background-200 p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {topics.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onTopicChange(item)}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors md:text-sm ${
                topic === item
                  ? "bg-foreground-900 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="hidden items-center gap-1 text-xs text-foreground-500 sm:flex">
            <i className="ri-list-check-2"></i>
            {resultCount} 篇结果
          </span>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100 md:hidden"
          >
            <i className="ri-filter-3-line"></i>
            筛选
          </button>
        </div>
      </div>

      <div className={`${open ? "block" : "hidden"} p-4 md:block`}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <span className="w-16 shrink-0 text-xs text-foreground-500">排序</span>
            <div className="flex flex-wrap gap-2">
              {sortOptions.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => onSortChange(item.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                    sort === item.value
                      ? "bg-accent-100 font-medium text-accent-800"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <span className="w-16 shrink-0 pt-1 text-xs text-foreground-500">标签</span>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => onTagToggle(tag)}
                  className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
                    activeTags.includes(tag)
                      ? "border-accent-500 bg-accent-500 text-background-50"
                      : "border-background-300 text-foreground-600 hover:border-accent-300 hover:bg-accent-50"
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-background-100 pt-3">
            <span className="text-xs text-foreground-500">
              {hasFilter ? "已应用筛选条件" : "当前展示全部文章"}
            </span>
            <button
              type="button"
              onClick={onReset}
              disabled={!hasFilter}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs text-foreground-600 transition-colors hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-refresh-line"></i>
              清空筛选
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}